import {
  ParentWaitlistedClassesQuery,
  ParentWaitlistedClassesQueryVariables,
} from "@outschool/gql-frontend-generated";
import SectionCls from "@outschool/section";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { compact, max, partition } from "lodash";
import sortBy from "lodash/sortBy";
import React from "react";

import * as Activity from "../../shared/Activity";
import * as Meeting from "../../shared/Meeting";

const Section = new SectionCls();

export type WaitlistSeatType = NonNullable<
  ParentWaitlistedClassesQuery["waitlistSeatsForParent"][number]
>;
export type WaitlistSectionType = WaitlistSeatType["section"];
export type WaitlistLeaderType = WaitlistSectionType["leader"];
export type WaitlistLearnerType = WaitlistSeatType["learner"];

export interface WaitlistedClassItem {
  key: string;
  learner: WaitlistLearnerType;
  section: WaitlistSectionType;
  waitlistSeats: WaitlistSeatType[];
  availableSpaces?: number;
}

function useWaitlistedClasses() {
  const { currentUser, currentUserIsLoading } = useSession();

  // We skip the query if no parentUid, so we can assert this is non-null
  const parentUid = currentUser?.uid!;

  const {
    loading: queryLoading,
    data,
    error,
  } = useQueryWithPreviousData<
    ParentWaitlistedClassesQuery,
    ParentWaitlistedClassesQueryVariables
  >(ParentWaitlistedClasses, {
    variables: { parentUid },
    skip: currentUserIsLoading || !parentUid,
  });

  const loading = queryLoading || currentUserIsLoading;

  // Each "WaitlistedClassItem" data structure corresponds
  // to an individual item rendered on the page. A item can contain
  // an individual or multiple waitlist seats.
  const waitlistItems = React.useMemo(() => {
    const filteredWaitlistSeats = data?.waitlistSeatsForParent.filter(
      seat => !seat?.section.canceled_at && !seat?.section.deleted_at,
    );

    const waitlistSeats = compact(filteredWaitlistSeats);

    // Separate out seats where the section has available spaces
    const [seatsWithAvailability, seatsWithoutAvailability] = partition(
      waitlistSeats,
      seat => Section.publicallyAvailableSpaces(seat.section) !== 0,
    );

    // Build item for each seat with available spaces
    const itemsWithAvailability =
      seatsWithAvailability.map<WaitlistedClassItem>(seat => ({
        key: getKeyForWaitlistMap(seat),
        learner: seat.learner,
        section: seat.section,
        waitlistSeats: [seat],
        availableSpaces: Section.publicallyAvailableSpaces(seat.section),
      }));

    // Build item for each unique learner+activity pair
    const itemMap = new Map<string, WaitlistedClassItem>();
    seatsWithoutAvailability.forEach(seat => {
      const key = getKeyForWaitlistMap(seat);

      const { section, learner } = seat;
      const current: WaitlistedClassItem = itemMap.get(key) ?? {
        key,
        learner,
        section,
        waitlistSeats: [],
      };
      current.waitlistSeats.push(seat);
      itemMap.set(key, { ...current });
    });
    const itemsWithoutAvailability = [...itemMap.values()];

    // Sort the items that don't have available spaces
    const sortedItemsWithoutAvailability = sortBy(itemsWithoutAvailability, [
      // Sort waitlist seats into groups with other activities, sorted by
      // whichever activity has the next occurring meeting.
      item => {
        const itemsInSameActivity = itemsWithoutAvailability.filter(
          ({ section }) => section.activity.uid === item.section.activity.uid,
        );
        const nextUpcomingMeetings = itemsInSameActivity.map(
          ({ waitlistSeats }) => max(waitlistSeats.map(getNextMeeting)),
        );
        return max(nextUpcomingMeetings);
      },
    ]);

    // Return all items
    return [...itemsWithAvailability, ...sortedItemsWithoutAvailability];
  }, [data]);

  return {
    loading,
    waitlistItems,
    error,
  };
}

function getNextMeeting({ section }: WaitlistSeatType) {
  const { nextOngoingMeeting, meetings, activity } = section;
  if (nextOngoingMeeting) {
    return nextOngoingMeeting.start_time;
  }
  if (activity && Activity.isFlexSchedule(activity)) {
    return section?.start_time;
  }
  const nextMeeting = meetings.length
    ? Meeting.currentOrNextMeeting(meetings)
    : null;
  // If there are no next meetings we will order to the end (like groups),
  // otherwise use next meeting start time
  return nextMeeting?.start_time;
}

/**
 * Generates a string that uniquely indexes a single waitlist item.
 * Determines the grouping for items, (learner+activity, and learner+
 * activity+section for items with availability).
 */
function getKeyForWaitlistMap(seat: WaitlistSeatType) {
  const learnerUid = seat.learner.uid;
  const activityUid = seat.section.activity.uid;
  const hasSpaces = seat.section.availableSpaces !== 0;
  const sectionUid = hasSpaces ? `_${seat.section.uid}` : "";
  return `${learnerUid}_${activityUid}${sectionUid}`;
}

export const ParentWaitlistedClasses = gql`
  query ParentWaitlistedClasses($parentUid: ID!) {
    waitlistSeatsForParent(userUid: $parentUid) {
      uid
      enrollment {
        uid
      }
      learner {
        uid
        name
        avatar
        age
      }
      section {
        uid
        canceled_at
        deleted_at
        end_time
        price_cents
        availableSpaces
        published_at
        start_time
        waitlistSeatsCount
        meetingDays
        activity {
          uid
          duration_weeks
          size_max
          hasTeacherSchedule
          isClub
          isOneTime
          is_ongoing_weekly
          price_cents
          published_at
          title
          titleTranslated
          slug_id
          weekly_meetings
          isFlexSchedule
          isSelfPaced
          details {
            photo
          }
          leader {
            uid
            name
            photo
            leader_link
            timeZone
          }
        }
        leader {
          uid
          name
          photo
          leader_link
          timeZone
        }
        meetings {
          uid
          start_time
          end_time
        }
        nextOngoingMeeting {
          uid
          start_time
          end_time
        }
      }
    }
  }
`;

export default useWaitlistedClasses;
